import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectEmployees } from "src/stores/employeeSlice";
import { selectProjects } from "src/stores/projectSlice";
import { notify } from '../utils/ToastUtils';
import ApiClient from "../ApiClient";
import ms from "ms";
import moment from "moment";
import GroupedTimesheetTable from '../components/GroupedTimesheetTable';
import { selectUser } from "../stores/userSlice";
import DsrArchieve from "./DsrArchieve";
import { Calendar, FileText, CalendarDays, Clock, Briefcase, RotateCcw, Search, List, BarChart2, User, Folder, Trash2, X } from 'lucide-react';
import { TextField, Select, MenuItem, Tooltip, Autocomplete, Chip } from '@mui/material';
import TimesheetAdminDashboard from '../components/TimesheetAdminDashboard';
import { ALL_LOCATIONS } from 'src/utils/constants';
import { cleanHtml } from "src/Utils";

const htmlToRender = (Description) => cleanHtml(Description);

function TimeSheetSA() {
  const Kaitongo = process.env.REACT_APP_KAITONGO_MANAGER ? process.env.REACT_APP_KAITONGO_MANAGER.split(",") : [];
  const initialState = {
    selectedEmployees: [],
    selectedProject: "All",
    location: "All",
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  };
  const user = useSelector(selectUser);
  const [empCode, setEmpCode] = useState("");
  const [state, setState] = useState(initialState);
  const [rows, setRows] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [rawApiData, setRawApiData] = useState([]);
  let timesheetData = [];
  
  const employees = useSelector(selectEmployees);
  const [KaitongoEmp, setKaitongoEmp] = useState([]);
  const projects = useSelector(selectProjects);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const sortedProjects = projects
    .slice()
    .sort((a, b) =>
      a.PROJECT_NAME > b.PROJECT_NAME
        ? 1
        : b.PROJECT_NAME > a.PROJECT_NAME
        ? -1
        : 0
    )
    .filter((el) => el.PROJECT_STATUS === "Open");

  useEffect(() => {
    if (user !== null) {
      if (Kaitongo.includes(user.emp_code)) {
        setEmpCode(user.emp_code);
      } else {
        setEmpCode("");
      }
    }
  }, [user]);

  useEffect(() => {
    if (empCode) {
      setKaitongoEmp(
        employees
          .filter((ele) => ele.EMP_WORK_EMAIL.indexOf("@kaitongo.com") !== -1)
          ?.map((ele) => ele.EMP_CODE)
      );
    }
  }, [empCode, employees]);

  useEffect(() => {
    if (!initialFetchDone && state.startDate && state.endDate) {
        getTimesheet();
        setInitialFetchDone(true);
    }
  }, [state.startDate, state.endDate, initialFetchDone]);

  function handleChange(evt) {
    const value = evt.target.value;
    if (evt.target.name === "location") {
        setState({
            ...state,
            location: value,
            selectedEmployees: []
        });
    } else {
        setState({
            ...state,
            [evt.target.name]: value,
        });
    }
  }

  const getProjectName = (params) => {
    let code = `${params.row.PROJECT_CODE}`;
    const project = projects?.find((el) => el?.PROJECT_CODE === code);
    return project?.PROJECT_NAME;
  };

  const getEmpName = (params) => {
    let code = `${params.row.CODE}`;
    const emp = employees?.find((el) => el?.EMP_CODE === code);
    return emp?.EMP_NAME;
  };

  const renderTruncatedCell = (params) => {
    let displayValue = params.value;
    
    // Explicitly compute value getters to ensure data is always present
    if (params.field === 'PROJECT_CODE') {
        displayValue = getProjectName(params);
    } else if (params.field === 'CODE') {
        displayValue = getEmpName(params);
    }

    return (
      <Tooltip title={displayValue || ""} placement="top" arrow>
        <span className="block truncate w-full overflow-hidden text-ellipsis whitespace-nowrap min-w-0">{displayValue || "-"}</span>
      </Tooltip>
    );
  };

  const getTimesheet = async () => {
    let errorMsg;
    if (!state.startDate && !state.endDate)
      errorMsg = "Start & End Dates are required";
      
    if (!errorMsg) {
      if (new Date(state.startDate) <= new Date(state.endDate)) {
        setIsDisabled(true);
        setRawApiData([]);
        setRows([]);
        try {
          const { data } = await ApiClient.post(
            `${process.env.REACT_APP_BASE_URL}/admin/adminTimeSheet`,
            {
              startDt: state.startDate,
              endDt: state.endDate,
              empCodes: state.selectedEmployees.length > 0 ? state.selectedEmployees.map((emp) => emp.EMP_CODE || emp.emp_code || emp.EMP_ID || emp.emp_id) : "All",
              projectCode: state.selectedProject,
              location: state.location,
            }
          );
          if (data.length === 0) {
            // notify.error("No matching records found");
            setRows([]);
            setRawApiData([]);
            setIsDisabled(false);
          } else {
            setRawApiData(data);
            let filteredArray = data.map(cur => ({
              ...cur,
              RawDescription: cur.Description,
              Description: htmlToRender(cur.Description),
              WORK_HOURS: Number(cur.WORK_HOURS)
            }));
            timesheetData = filteredArray;
            
            if (state.selectedEmployees.length > 0) {
              const selectedCodes = new Set(state.selectedEmployees.map((emp) => emp.EMP_CODE || emp.emp_code || emp.EMP_ID || emp.emp_id));
              let selectedEmployeeData = timesheetData?.filter((ele) => selectedCodes.has(ele.CODE) || selectedCodes.has(ele.EMP_CODE));
              if (empCode) {
                selectedEmployeeData = selectedEmployeeData.filter((ele) => KaitongoEmp.includes(ele.CODE));
              }
              setRows(selectedEmployeeData);
            } else if (empCode) {
              setRows(timesheetData?.filter((ele) => KaitongoEmp.includes(ele.CODE)));
            } else {
              setRows(timesheetData);
            }
            setIsDisabled(false);
          }
        } catch (error) {
          console.log(error);
          setIsDisabled(false);
          let errmsg = error?.response
            ? error?.response?.data?.message
              ? error?.response?.data?.message
              : `${error?.response?.status} - ${error?.response?.statusText}`
            : "Something went wrong. Please try again later";
          notify.error(errmsg);
        }
      } else notify.error("Start Date should be lesser than End Date");
    } else notify.error(`${errorMsg}`);
  };

  const initDateRange = () => {
    const minsec = ms("0d");
    const min_date = new Date(new Date(state.startDate) - minsec);
    const max_date = new Date();
    setMinDate(moment(min_date).format("YYYY-MM-DD"));
    setMaxDate(moment(max_date).format("YYYY-MM-DD"));
  };

  useEffect(() => {
    initDateRange();
  }, [state.startDate]);

  const renderProjectOptions = () => {
    if (empCode !== "") {
        // Kaitongo
        return sortedProjects
            ?.filter((ele) => ele.PROJECT_CODE == "PR000082")
            ?.map((el, index) => (
            <MenuItem key={index} value={el.PROJECT_CODE} style={{ whiteSpace: "normal" }}>
                <div className="text-[13px] leading-snug break-words">{el.PROJECT_NAME}</div>
            </MenuItem>
            ));
    } else {
        return sortedProjects?.map((el) => (
            <MenuItem key={el.PROJECT_CODE} value={el.PROJECT_CODE} style={{ whiteSpace: "normal" }}>
                <div className="text-[13px] leading-snug break-words">{el.PROJECT_NAME}</div>
            </MenuItem>
        ));
    }
  };

  const selectMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 250,
        maxWidth: 10,
      },
    },
  };

  return (
    <div className="flex flex-col w-full gap-3">
      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Top Tabs */}
        <div className="flex border-b border-slate-200 px-4 pt-1">
            <button
                onClick={() => setActiveTab('all')}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    activeTab === 'all'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
            >
                <List size={16} />
                All Entries
            </button>
            <button
                onClick={() => setActiveTab('summary')}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    activeTab === 'summary'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
            >
                <BarChart2 size={16} />
                Timesheet Summary
            </button>
        </div>

        <div className="p-6">

            <div className="flex flex-col">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-4 items-end">                    <div className="w-full min-w-0">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            Location
                        </label>
                        <Select
                            name="location"
                            value={state.location}
                            onChange={handleChange}
                            className="w-full bg-white text-sm"
                            size="small"
                            sx={{ '& .MuiSelect-select': { padding: '8px 14px', height: '22px', fontSize: '14px' } }}
                        >
                            <MenuItem value="All" sx={{ fontSize: '14px' }}>All Locations</MenuItem>
                            {ALL_LOCATIONS?.map(loc => (
                                <MenuItem key={loc.value} value={loc.value} sx={{ fontSize: '14px' }}>{loc.label}</MenuItem>
                            ))}
                        </Select>
                    </div>

                    <div className="w-full min-w-0">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            Employee
                        </label>
                        <Autocomplete
                            multiple
                            options={(employees || [])?.filter((el) => el.STATUS === "Active" && el.DSR_EXCEPTION !== "Yes" && (empCode === "" || (el.EMP_WORK_EMAIL || "").indexOf("@kaitongo.com") !== -1) && (state.location === "All" || el?.EMP_COUNTRY === state?.location))}
                            getOptionLabel={(option) => `${option.EMP_NAME} (${option.EMP_ID || option.EMP_CODE || ""})`}
                            value={state.selectedEmployees}
                            onChange={(event, newValue) => {
                                setState({
                                    ...state,
                                    selectedEmployees: newValue
                                });
                            }}
                            size="small"
                            className="w-full bg-white text-sm"
                            limitTags={1}
                            disableCloseOnSelect
                            renderTags={() => null}
                            isOptionEqualToValue={(option, value) => option.EMP_CODE === value.EMP_CODE}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    height: "38px",
                                    paddingTop: "0 !important",
                                    paddingBottom: "0 !important",
                                    fontSize: "14px"
                                },
                                "& .MuiAutocomplete-input": {
                                    paddingTop: "0 !important",
                                    paddingBottom: "0 !important",
                                },
                            }}
                               ListboxProps={{ sx: { fontSize: '14px' } }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={state.selectedEmployees.length > 0 ? `${state.selectedEmployees.length} Selected` : "All Employees"}
                                />
                            )}
                        />
                    </div>

                    <div className="w-full min-w-0">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            Project
                        </label>
                        <Autocomplete
                            options={[{ PROJECT_CODE: "All", PROJECT_NAME: "All Projects" }, ...(empCode !== "" ? (sortedProjects?.filter((ele) => ele.PROJECT_CODE === "PR000082") || []) : (sortedProjects || []))]}
                            getOptionLabel={(option) => option.PROJECT_NAME || ""}
                            value={
                                state.selectedProject === "All" 
                                    ? { PROJECT_CODE: "All", PROJECT_NAME: "All Projects" } 
                                    : (sortedProjects?.find(p => p.PROJECT_CODE === state.selectedProject) || null)
                            }
                            onChange={(event, newValue) => {
                                handleChange({ target: { name: 'selectedProject', value: newValue ? newValue.PROJECT_CODE : 'All' } });
                            }}
                            size="small"
                            className="w-full bg-white text-sm"
                            disableClearable
                            sx={{ '& .MuiOutlinedInput-root': { padding: '2px 8px', height: '38px' }, '& .MuiAutocomplete-input': { padding: '4px !important', fontSize: '14px' } }}
                            ListboxProps={{ style: { maxHeight: 250, fontSize: '14px' } }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params}
                                    placeholder="Select Project"
                                />
                            )}
                        />
                    </div>

                    <div className="w-full min-w-0">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            Start Date<span className="text-red-500">*</span>
                        </label>
                        <TextField
                            name="startDate"
                            type="date"
                            value={state.startDate}
                            className="w-full bg-white text-sm"
                            size="small"
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="w-full min-w-0">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            End Date<span className="text-red-500">*</span>
                        </label>
                        <TextField
                            name="endDate"
                            type="date"
                            value={state.endDate}
                            className="w-full bg-white text-sm"
                            size="small"
                            onChange={handleChange}
                            inputProps={{ min: minDate, max: maxDate }}
                            required
                        />
                    </div>

                    <div className="w-full flex justify-end lg:justify-start gap-3 sm:col-span-2 lg:col-span-1 mt-2 lg:mt-0">
                        <button
                            type="button"
                            onClick={getTimesheet}
                            disabled={isDisabled}
                            className="text-sm h-[37px] px-5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm flex items-center justify-center gap-2 whitespace-nowrap"
                            style={{ opacity: isDisabled ? "50%" : "100%" }}
                        >
                            {isDisabled ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <Search size={16} />
                                    Get Timesheet
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {state.selectedEmployees.length > 0 && (
                    <>
                        <hr className="my-5 border-slate-200" />
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                            <div className="flex flex-wrap gap-2 flex-1 items-center">
                                <span className="text-xs font-bold text-slate-500 uppercase mr-1">Selected Employees:</span>
                                {state.selectedEmployees.map((emp) => (
                                    <Chip 
                                        key={emp.EMP_CODE} 
                                        label={emp.EMP_NAME} 
                                        size="small" 
                                        className='font-semibold'
                                        onDelete={() => {
                                            setState({
                                                ...state,
                                                selectedEmployees: state.selectedEmployees.filter(e => e.EMP_CODE !== emp.EMP_CODE)
                                            })
                                        }}
                                        deleteIcon={<X size={14} strokeWidth={2.5} />}
                                        sx={{ 
                                            backgroundColor: '#eff6ff', 
                                            color: '#2563eb', 
                                            '& .MuiChip-deleteIcon': {
                                                color: '#3b82f6', 
                                                '&:hover': { color: '#1d4ed8' } 
                                            }
                                        }}
                                    />
                                ))}
                                <button 
                                    onClick={() => setState({ ...state, selectedEmployees: [] })}
                                    className="text-[12px] text-red-500 hover:text-red-700 font-medium ml-2 underline underline-offset-2"
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
      </div>

      {activeTab === 'all' ? (
          <>
            {/* Table Section */}
            <GroupedTimesheetTable data={rows} projects={projects} isLoading={isDisabled} employees={employees} />
          </>
      ) : (
          <TimesheetAdminDashboard rawTimesheetData={rawApiData} projects={projects} employees={employees} isApiLoading={isDisabled} />
      )}

      {/* DSR Archive block for Kaitongo */}
      {empCode !== "" && <DsrArchieve />}
    </div>
  );
}

export default TimeSheetSA;

